import AddFileIcon from 'icons/AddFile.icon';
import AskQuestionIcon from 'icons/AskQuestion.icon';
import ChatIcon from 'icons/Chat.icon';
import ClockIcon from 'icons/Clock.icon';
import DocumentWithPencilIcon from 'icons/DocumentWithPencil.icon';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import LinkIcon from 'icons/Link.icon';
import NoFileIcon from 'icons/NoFile.icon';
import QuestionFileIcon from 'icons/QuestionFile.icon';
import QuestionMarkCircle from 'icons/QuestionMarkCircle.icon';
import { COLOR_VARIANT, ColorVariants } from 'models/Colors';
import { IconProp } from 'models/Icon';
import {
  SuggestedActionType,
  SUGGESTED_ACTION_TYPE,
} from 'router/subrouters/DataRoom/models/Dashboard';

export const ACTIVE_SUGGESTION_ICON: Record<SuggestedActionType, IconProp> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: AddFileIcon,
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]: NoFileIcon,
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]: AskQuestionIcon,
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: QuestionMarkCircle,
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]: GroupUsersIcon,
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]: ClockIcon,
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]: QuestionFileIcon,
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: LinkIcon,
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: ChatIcon,
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: DocumentWithPencilIcon,
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: DocumentWithPencilIcon,
};

export const COLOR_SUGGESTION_ICON: Record<SuggestedActionType, ColorVariants> =
  {
    [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: COLOR_VARIANT.RED,
    [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]: COLOR_VARIANT.ORANGE,
    [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]: COLOR_VARIANT.ORANGE,
    [SUGGESTED_ACTION_TYPE.NO_FAQS]: COLOR_VARIANT.ORANGE,
    [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]: COLOR_VARIANT.RED,
    [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]: COLOR_VARIANT.RED,
    [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]: COLOR_VARIANT.RED,
    [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: COLOR_VARIANT.RED,
    [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: COLOR_VARIANT.BLUE,
    [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: COLOR_VARIANT.ORANGE,
    [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: COLOR_VARIANT.GREEN,
  };

export const ACTIVE_SUGGESTION_TITLE: Record<SuggestedActionType, string> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: 'suggestedDataRoomEmpty',
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]:
    'suggestedNoFilesAddedIn10Days',
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]: 'suggestedUnrepliedQuestions',
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: 'suggestedNoFaqs',
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]: 'suggestedWaitingRoomCount',
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]: 'suggestedSecureLinkExpiring',
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]: 'suggestedNewFileRequestSubmitted',
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: 'secureLinkEmpty',
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: 'suggestedUnreadMessages',
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: 'suggestedUnreadLeadNotes',
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: 'suggestedUnreadDealNotes',
};

export const ACTIVE_SUGGESTION_DESCRIPTION: Record<
  SuggestedActionType,
  string
> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: 'suggestedDataRoomEmptyDescription',
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]:
    'suggestedNoFilesAddedIn10DaysDescription',
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]:
    'suggestedUnrepliedQuestionsDescription',
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: 'suggestedNoFaqsDescription',
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]:
    'suggestedWaitingRoomDescription',
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]:
    'suggestedSecureLinkExpiringDescription',
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]:
    'suggestedNewFileRequestSubmittedDescription',
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: 'secureLinkEmptyDescription',
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: 'suggestedUnreadMessagesDescription',
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]:
    'suggestedUnreadLeadNotesDescription',
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]:
    'suggestedUnreadDealNotesDescription',
};

export const ACTIVE_SUGGESTION_ACTION: Record<SuggestedActionType, string> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: 'startAddingFiles',
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]: 'addMoreFiles',
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]:
    'suggestedUnrepliedQuestionsAction',
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: 'addFaqs',
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]: 'goToWaitingRoom',
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]:
    'suggestedSecureLinksExpiringSoonAction',
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]: 'goToFileRequests',
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: 'goToSecureLinks',
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: 'suggestedUnreadMessagesAction',
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: 'suggestedUnreadLeadNotesAction',
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: 'suggestedUnreadDealNotesAction',
};

export const ACTIONED_SUGGESTED_ACTION_NAME: Record<
  SuggestedActionType,
  string
> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]:
    'actionedSuggestedActionEmptyDataRoomName',
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]:
    'actionedSuggestedActionNoNewFilesName',
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]:
    'actionedSuggestedActionUnrepliedQuestionsName',
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: 'actionedSuggestedActionNoFaqsName',
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]:
    'actionedSuggestedActionWaitingRoomName',
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]:
    'actionedSuggestedActionSecureLinksExpiringName',
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]:
    'actionedSuggestedActionNewFileRequestName',
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]:
    'actionedSuggestedActionLinkEmptyName',
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]:
    'actionedSuggestedActionUnreadMessagesName',
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]:
    'actionedSuggestedActionUnreadLeadNotesName',
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]:
    'actionedSuggestedActionUnreadDealNotesName',
};

export const ACTIONED_SUGGESTED_ACTION_ACTION: Record<
  SuggestedActionType,
  string
> = {
  [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]:
    'actionedSuggestedActionFilesUploaded',
  [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]:
    'actionedSuggestedActionFilesUploaded',
  [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]:
    'actionedSuggestedActionUnrepliedQuestionsAction',
  [SUGGESTED_ACTION_TYPE.NO_FAQS]: 'actionedSuggestedActionNoFaqsAction',
  [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]:
    'actionedSuggestedActionLinkVisited',
  [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]:
    'actionedSuggestedActionSecureLinksExpiringAction',
  [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]:
    'actionedSuggestedActionLinkVisited',
  [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]:
    'actionedSuggestedActionLinkEmpty',
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]:
    'actionedSuggestedActionUnreadMessagesAction',
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]:
    'actionedSuggestedActionUnreadLeadNotesAction',
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]:
    'actionedSuggestedActionUnreadDealNotesAction',
};
